/**
 * BatchComparisonModal
 *
 * Side-by-side property comparison overlay for selected batch results.
 * Supports 2-3 properties (per usePropertyComparison MAX_COMPARISON limit).
 *
 * Features:
 *  - Fixed overlay (z-50), semi-transparent backdrop, centered modal ~90vw max-w-4xl
 *  - Side-by-side columns, one per property
 *  - Each column: address header, composite score + rank badge, key metrics table
 *  - Winner highlight: gold/bold for best value per metric row
 *  - Remove button (X) on each column header
 *  - Close button top-right + click-outside-to-close
 *  - Responsive: stacks vertically on mobile (< md)
 */

import { X, Building2 } from 'lucide-react';
import type { BatchPropertyResult } from '../../types/batch';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BatchComparisonModalProps {
  open: boolean;
  onClose: () => void;
  properties: BatchPropertyResult[];
  onRemove: (id: number) => void;
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function fmt(value: number | null | undefined, type: 'currency' | 'percent' | 'composite' | 'em' | 'int'): string {
  if (value == null) return '—';
  switch (type) {
    case 'currency':
      if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
      if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
      return `$${value.toFixed(0)}`;
    case 'percent':
      if (Math.abs(value) > 1) return `${value.toFixed(1)}%`;
      return `${(value * 100).toFixed(1)}%`;
    case 'composite':
      return value.toFixed(1);
    case 'em':
      return `${value.toFixed(2)}x`;
    case 'int':
      return String(Math.round(value));
    default:
      return String(value);
  }
}

// ---------------------------------------------------------------------------
// Verdict pill
// ---------------------------------------------------------------------------

function VerdictPill({ verdict }: { verdict: string | null | undefined }) {
  if (verdict == null) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-500/15 text-text-secondary border border-border">
        PENDING
      </span>
    );
  }
  const colors: Record<string, string> = {
    'GO': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    'NO-GO': 'bg-red-500/15 text-red-400 border-red-500/30',
    'CONDITIONAL': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  };
  const cls = colors[verdict] ?? 'bg-gray-500/15 text-text-secondary border-border';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border ${cls}`}>
      {verdict}
    </span>
  );
}

// ---------------------------------------------------------------------------
// MetricRow — one row in the comparison table
// ---------------------------------------------------------------------------

interface MetricRowDef {
  label: string;
  getValue: (p: BatchPropertyResult) => number | null | undefined;
  formatType: 'currency' | 'percent' | 'composite' | 'em' | 'int';
  /**
   * 'highest' = bigger is better (green winner, red loser)
   * 'lowest'  = smaller is better
   * 'none'    = no highlighting
   */
  best: 'highest' | 'lowest' | 'none';
}

const METRIC_ROWS: MetricRowDef[] = [
  { label: 'Composite Score', getValue: (p) => p.composite_score, formatType: 'composite', best: 'highest' },
  { label: 'IRR 5yr', getValue: (p) => p.irr_5yr, formatType: 'percent', best: 'highest' },
  { label: 'Equity Multiple', getValue: (p) => p.equity_multiple, formatType: 'em', best: 'highest' },
  { label: 'Cap Rate', getValue: (p) => p.cap_rate, formatType: 'percent', best: 'highest' },
  { label: 'Zandi Score', getValue: (p) => p.zandi_composite, formatType: 'composite', best: 'highest' },
  { label: 'NOI', getValue: (p) => p.noi, formatType: 'currency', best: 'highest' },
  { label: 'Offer Price (High)', getValue: (p) => p.offer_range_high, formatType: 'currency', best: 'none' },
  { label: 'Offer Price (Low)', getValue: (p) => p.offer_range_low, formatType: 'currency', best: 'none' },
  { label: 'Avg CoC', getValue: (p) => p.avg_coc, formatType: 'percent', best: 'highest' },
  { label: 'Units', getValue: (p) => p.total_units, formatType: 'int', best: 'none' },
];

function MetricRow({
  def,
  properties,
}: {
  def: MetricRowDef;
  properties: BatchPropertyResult[];
}) {
  const rawValues = properties.map((p) => def.getValue(p) ?? null);

  // Find best column index
  let bestIdx: number | null = null;
  if (def.best !== 'none') {
    const validPairs = rawValues
      .map((v, i) => ({ v, i }))
      .filter((x) => x.v !== null) as { v: number; i: number }[];

    if (validPairs.length > 1) {
      bestIdx =
        def.best === 'highest'
          ? validPairs.reduce((acc, x) => (x.v > validPairs[acc].v ? x.i : acc), validPairs[0].i)
          : validPairs.reduce((acc, x) => (x.v < validPairs[acc].v ? x.i : acc), validPairs[0].i);
    }
  }

  return (
    <tr className="border-b border-border/50 last:border-b-0">
      <td className="px-3 py-2.5 text-[11px] font-medium text-text-secondary bg-bg-secondary whitespace-nowrap">
        {def.label}
      </td>
      {rawValues.map((val, idx) => {
        const isBest = bestIdx === idx;
        return (
          <td
            key={idx}
            className={`px-3 py-2.5 text-xs font-mono text-center transition-colors
              ${isBest ? 'text-accent font-bold bg-accent/5' : 'text-text-primary'}
            `}
          >
            {fmt(val, def.formatType)}
          </td>
        );
      })}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BatchComparisonModal({
  open,
  onClose,
  properties,
  onRemove,
}: BatchComparisonModalProps) {
  if (!open || properties.length === 0) return null;

  const colWidth = properties.length === 2 ? 'w-1/2' : 'w-1/3';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop — click outside to close */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-primary border border-border rounded-xl shadow-2xl w-[90vw] max-w-4xl max-h-[88vh] mx-4 flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border bg-bg-secondary flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <Building2 className="w-5 h-5 text-accent" />
            <h2 className="text-sm font-semibold text-text-primary">
              Compare Properties ({properties.length})
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-text-secondary hover:text-text-primary hover:bg-bg-card rounded-lg transition-colors"
            aria-label="Close comparison"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content — scrollable */}
        <div className="flex-1 overflow-auto p-4">

          {/* Property column headers */}
          <div className="flex gap-3 mb-4">
            {/* Empty label column */}
            <div className="w-36 flex-shrink-0" />
            {/* Property columns */}
            {properties.map((prop, idx) => (
              <div
                key={prop.property_id}
                className={`flex-1 ${colWidth} bg-bg-card border border-border rounded-lg p-3 relative group`}
              >
                {/* Remove button */}
                <button
                  onClick={() => onRemove(prop.property_id)}
                  className="absolute top-2 right-2 p-1 text-text-secondary hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                  aria-label="Remove from comparison"
                >
                  <X className="w-3.5 h-3.5" />
                </button>

                {/* Rank badge */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent/10 text-accent text-[10px] font-bold flex-shrink-0">
                    {idx + 1}
                  </span>
                  <span className="text-[11px] text-text-secondary truncate font-mono">
                    #{idx + 1}
                  </span>
                </div>

                {/* Address */}
                <p className="text-xs font-semibold text-text-primary leading-tight mb-1.5 line-clamp-2">
                  {prop.address ?? '—'}
                </p>

                {/* Neighborhood */}
                {prop.neighborhood && (
                  <p className="text-[11px] text-text-secondary mb-2 truncate">{prop.neighborhood}</p>
                )}

                {/* Verdict */}
                <VerdictPill verdict={prop.deal_verdict} />
              </div>
            ))}
          </div>

          {/* Metrics table */}
          <div className="bg-bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-3 py-2 border-b border-border bg-bg-secondary">
              <span className="text-[10px] uppercase tracking-wider text-accent font-semibold">
                Metrics Comparison
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-text-secondary uppercase tracking-wide bg-bg-secondary w-36">
                      Metric
                    </th>
                    {properties.map((prop) => (
                      <th
                        key={prop.property_id}
                        className="px-3 py-2 text-center text-[10px] font-semibold text-text-secondary uppercase tracking-wide"
                      >
                        {prop.address?.split(',')[0] ?? `#${prop.property_id}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {METRIC_ROWS.map((def) => (
                    <MetricRow key={def.label} def={def} properties={properties} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-5 py-3 border-t border-border bg-bg-secondary flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-text-primary bg-bg-card border border-border rounded-lg hover:border-accent/50 hover:text-accent transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default BatchComparisonModal;
